<template>
  <b-card>
    <h3 class="section-label">
      {{ $t('Central Webhook Configuration') }}
    </h3>
    <validation-observer ref="centralWebhookConfigRules">
      <b-form @submit.prevent="saveCentralWebhookConfig">
        <b-row class="mt-2">
          <b-col md="6">
            <InputField
              id="webhook-url"
              v-model="centralWebhookFormData.configuration.webhookUrl"
              :label="$t('Webhook URL')"
              :placeholder="$t('Enter webhook url')"
              type="text"
              :error-name="$t('Webhook URL')"
              rules="required"
              :readonly="isReadonly"
            />
          </b-col>

          <b-col md="6">
            <InputField
              id="webhook-username"
              v-model="centralWebhookFormData.configuration.webhookUserName"
              :label="$t('Webhook Username')"
              :placeholder="$t('Enter Webhook Username')"
              type="text"
              :readonly="isReadonly"
            />
          </b-col>

          <b-col md="6">
            <InputPasswordField
              id="webhook-password"
              v-model="centralWebhookFormData.configuration.webhookPassword"
              :label="$t('Webhook Password')"
              :placeholder="$t('Enter Webhook Password')"
              :readonly="isReadonly"
            />
          </b-col>
        </b-row>

        <div class="text-center">
          <Button
            v-if="getUserPermission('MODIFY_SYSTEM_SETUP')"
            :label="buttonLabel"
            :loading="isLoading"
            :disabled="isDisabled"
            size="lg"
            @click="saveCentralWebhookConfig"
          />
        </div>
      </b-form>
    </validation-observer>
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BRow, BCol, BCard, BForm,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import { required, maxValue, positive } from '@validations'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../../forms/InputField.vue'
import InputPasswordField from '../../../../forms/InputPasswordField.vue'
import Button from '../../../../components/button/Button.vue'
import { operationPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    InputField,
    InputPasswordField,
    BForm,
    ValidationObserver,
    Button,
    FullScreenSpinner,
  },
  directives: {
    Ripple,
  },
  mixins: [sweetAlert, permissionMixin],
  data() {
    return {
      required,
      positive,
      maxValue,
      isDisabled: false,
      isReadonly: true,
      isEditable: true,
      isLoading: false,
      showSpinner: false,
      buttonLabel: this.$t('Edit'),
      centralWebhookFormData: {
        configuration: {
          webhookUrl: '',
          webhookUserName: '',
          webhookPassword: '',
        },
      },
    }
  },
  mounted() {
    this.fetchCentralWebhookConfig()
  },
  methods: {
    ...mapActions({
      getWebhookConfig: 'operations/getWebhookConfigList',
      updateWebhookConfig: 'operations/updateWebhookConfigAction',
    }),
    saveCentralWebhookConfig() {
      if (this.buttonLabel === this.$t('Save Changes')) {
        this.$refs.centralWebhookConfigRules.validate().then(success => {
          if (success) {
            this.confirm(this.handleSubmit, { text: this.$t('Are you sure you want to Update this Webhook Configuration?') })
          }
        })
      } else {
        this.isReadonly = false
        this.isEditable = true
        this.buttonLabel = this.$t('Save Changes')
      }
    },
    async handleSubmit() {
      try {
        this.isLoading = true
        this.isDisabled = true
        const formData = {
          configuration: {
            webhookUrl: this.centralWebhookFormData.configuration.webhookUrl,
            webhookUserName: this.centralWebhookFormData.configuration.webhookUserName,
            webhookPassword: this.centralWebhookFormData.configuration.webhookPassword,
          },
        }
        const response = await this.updateWebhookConfig({ formData, permission: operationPermissions.MODIFY_SYSTEM_SETUP })
        const { status } = await response
        if (status === 200) {
          this.fetchCentralWebhookConfig()
          this.alertSuccess({ text: this.$t('Your request to Update Webhook Configuration was successful') })
          this.isReadonly = true
          this.isEditable = true
          this.isDisabled = false
          this.isLoading = false
          this.buttonLabel = 'Edit'
        }
      } catch (error) {
        const requestText = this.$t('Your request to Update Webhook Configuration failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${requestText} ${responseError.errorMessage}` })
        })
      } finally {
        this.isEditable = true
        this.isDisabled = false
        this.isLoading = false
      }
    },
    async fetchCentralWebhookConfig() {
      try {
        this.showSpinner = true
        const response = await this.getWebhookConfig({ permission: operationPermissions.VIEW_SYSTEM_SETUP })
        const { status, data } = response
        if (status === 200 && data) {
          this.centralWebhookFormData.configuration = {
            webhookUrl: data.institutionConfiguration.webhookUrl,
            webhookUserName: data.institutionConfiguration.webhookUserName,
            webhookPassword: data.institutionConfiguration.webhookPassword,
          }
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
  },
}
</script>
<style scoped>
.custom-checkbox:last-child {
  margin-left: 20px !important;
}
</style>
